import React, { useState, useEffect, useRef } from 'react';
import shark from '../../resources/shark.png';
import mirrorShark from '../../resources/mirrorshark.png';
import Button from 'react-bootstrap/Button';
import education from '../../resources/Home_Page/Education.png';
import workExperience from '../../resources/Home_Page/workExperience.png';
import EducationModal from './Modals/educationModal';
import ExperienceModal from './Modals/experienceModal';

// If an image fails (e.g. a flaky mobile connection, or Safari dropping it while
// the page sat in the back/forward cache), try once more with a fresh request.
function retryImage(event) {
  const img = event.currentTarget;
  if (img.dataset.retried) return;
  img.dataset.retried = 'true';
  img.src = `${img.getAttribute('src').split('?')[0]}?retry=${Date.now()}`;
}

// Re-request any shark image that ended up broken (loaded but with no pixels).
function repairSharkImages() {
  document.querySelectorAll('.shark-container').forEach((img) => {
    if (img.complete && img.naturalWidth === 0) {
      const src = img.getAttribute('src');
      img.removeAttribute('src');
      img.setAttribute('src', src);
    }
  });
}

// How far the shark slides toward the cursor (px), and how quickly it catches up.
const MAX_SLIDE_LEFT = 60;
const MAX_SLIDE_RIGHT = 150;
const GLIDE_SPEED = 0.08;

function Main() {
  // ============================================================
  // SHARK — follows the cursor and turns to face the way it moves
  // The glide is applied straight to the element, and the animation loop
  // sleeps once the shark has caught up, so nothing re-renders per frame
  // (only a change of facing direction updates React state).
  // ============================================================
  const sharkRef = useRef(null);
  const [facingRight, setFacingRight] = useState(false);

  useEffect(() => {
    let offset = 0;
    let target = 0;
    let lastX = 0;
    let frameId = null;

    const step = () => {
      offset += (target - offset) * GLIDE_SPEED;
      if (Math.abs(target - offset) < 0.05) offset = target;
      if (sharkRef.current) sharkRef.current.style.transform = `translateX(${offset}px)`;
      frameId = offset === target ? null : requestAnimationFrame(step);
    };

    const onPointer = (event) => {
      const { clientX } = event;
      const ratio = clientX / window.innerWidth - 0.5;
      target = ratio < 0 ? ratio * 2 * MAX_SLIDE_LEFT : ratio * 2 * MAX_SLIDE_RIGHT;

      if (Math.abs(clientX - lastX) > 2) {
        setFacingRight(clientX > lastX);
        lastX = clientX;
      }
      if (frameId === null) frameId = requestAnimationFrame(step);
    };

    document.addEventListener('mousemove', onPointer);
    document.addEventListener('mouseenter', onPointer);
    return () => {
      document.removeEventListener('mousemove', onPointer);
      document.removeEventListener('mouseenter', onPointer);
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, []);

  // ============================================================
  // SHARK — Safari bfcache fix
  // If the page is restored from back-forward cache (e.g. after
  // visiting the resume PDF and hitting back), force the swim
  // animation to restart cleanly instead of resuming mid-frame.
  // ============================================================
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        document.querySelectorAll('.shark-swim-wrapper').forEach((el) => {
          el.style.animation = 'none';
          void el.offsetHeight;
          el.style.animation = '';
        });
      }
      repairSharkImages();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') repairSharkImages();
    };
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // ============================================================
  // MODALS — gallery card visibility state
  // ============================================================
  const [educationModalShow, setEducationModalShow] = useState(false);
  const [experienceModalShow, setExperienceModalShow] = useState(false);

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <>
      {/* ---------- Hero section: heading + shark ---------- */}
      <div className="main" id="main">
        <div className="topLetterContainer">
          <div className="letterContainer">
            <h1 id="letter">K</h1>
            <h1 id="letter">E</h1>
            <h1 id="letter">N</h1>
            <h1 id="letter">
              <a
                href="/investing_playbook"
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                N
              </a>
            </h1>
            <h1 id="letter">
              <a
                href="/evolvingwithbooks"
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                E
              </a>
            </h1>
            <h1 id="letter">
              <a
                href="/travel"
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                T
              </a>
            </h1>
            <h1 id="letter">H</h1>
          </div>

          <div className="paragraph">
            <p>
              is a product owner, programme manager, and the adaptable
              bridge between strategy and delivery.
            </p>
          </div>
        </div>

        {/* ---------- Shark ---------- */}
        <div ref={sharkRef} className="wowFactor vertical-bottom">
          <a href="/megladonjourney">
            <div className="shark-swim-wrapper">
              {/* Both directions stay loaded; turning just swaps which is visible,
                  so no image has to be fetched mid-swim. */}
              <img
                alt=""
                src={shark}
                className={`shark-container${facingRight ? ' shark-container--hidden' : ''}`}
                id="shark-container"
                onError={retryImage}
              />
              <img
                alt=""
                aria-hidden="true"
                src={mirrorShark}
                className={`shark-container${facingRight ? '' : ' shark-container--hidden'}`}
                onError={retryImage}
              />
            </div>
            <div className="shark-mover" id="shark-mover"></div>
          </a>
        </div>
      </div>

      {/* ---------- Gallery / modals section ---------- */}
      <div className="about">
        <div className="containerHome">
          <div className="gallery gallery-pair">
            <div className="gallery-item" tabIndex="0">
              <Button
                variant="primary"
                className="gallery-buttimage"
                alt="education-btn"
              >
                <input
                  type="image"
                  className="gallery-image"
                  src={education}
                  alt="Education"
                  onClick={() => setEducationModalShow(true)}
                />
              </Button>
              <div className="gallery-item-info"></div>
            </div>

            <div className="gallery-item" tabIndex="0">
              <Button
                variant="primary"
                className="gallery-buttimage"
                alt="experience-btn"
              >
                <input
                  type="image"
                  className="gallery-image"
                  src={workExperience}
                  alt="Work experience"
                  onClick={() => setExperienceModalShow(true)}
                />
              </Button>
              <div className="gallery-item-info"></div>
            </div>
          </div>
        </div>
      </div>

      <EducationModal
        show={educationModalShow}
        onHide={() => setEducationModalShow(false)}
      />

      <ExperienceModal
        show={experienceModalShow}
        onHide={() => setExperienceModalShow(false)}
      />
    </>
  );
}

export default Main;